import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getAnalyticsCalendarEvents, updateAnalyticsCalendarEvents,
  gcalAuthUrl, gcalStatus, gcalDisconnect, gcalEvents, gcalEventErstellen, gcalEventLoeschen,
  msCalAuthUrl, msCalStatus, msCalDisconnect, msCalEvents, msCalEventErstellen, msCalEventLoeschen,
} from '../api';
import { GoogleCalendarIcon, MicrosoftCalendarIcon } from './AnalyticsIcons';
import { useConfirm } from '../components/ConfirmDialog';
import { MONATE_DE, DOW_DE } from './AnalyticsConstants';

function pad(n) { return String(n).padStart(2, '0'); }
function ymd(d)  { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function formatDay(ymdStr) {
  const [y, m, d] = ymdStr.split('-').map(Number);
  return `${d}. ${MONATE_DE[m - 1]} ${y}`;
}
function calendarMapToApi(map) {
  return Object.entries(map || {}).flatMap(([datum, entries]) => (
    Array.isArray(entries) ? entries : []
  ).map((entry, index) => ({
    id: entry.id || `local_${datum}_${index}_${Date.now()}`,
    datum,
    text: entry.title || entry.text || 'Termin',
    uhrzeit: entry.time || entry.uhrzeit || null,
    uhrzeit_bis: entry.uhrzeit_bis || null,
    type: 'local',
  })));
}
function apiToCalendarMap(events) {
  const map = {};
  (Array.isArray(events) ? events : []).forEach((event) => {
    if (!event?.datum || !event?.text) return;
    if (!map[event.datum]) map[event.datum] = [];
    map[event.datum].push({
      id: event.id,
      time: event.uhrzeit || '',
      title: event.text,
      sub: '',
      uhrzeit_bis: event.uhrzeit_bis || '',
    });
  });
  return map;
}

const inputBase = {
  width: '100%', padding: '7px 10px', boxSizing: 'border-box',
  border: '1.5px solid rgba(0,77,64,0.18)', borderRadius: 8,
  fontSize: 13, fontFamily: 'var(--an-font-body)', outline: 'none',
  background: '#fff', color: 'var(--an-ink)',
  transition: 'border-color 0.14s, box-shadow 0.14s',
};

export default function AnalyticsCalendar({ stellen = [] }) {
  const confirm = useConfirm();
  const [googleVerbunden, setGoogleVerbunden] = useState(false);
  const [msVerbunden, setMsVerbunden]         = useState(false);
  const [events, setEvents]                   = useState([]);
  const [viewDate, setViewDate]               = useState(new Date());
  const [selected, setSelected]               = useState(ymd(new Date()));

  const [localEvents, setLocalEvents] = useState({});
  const localEventMigrationDone = useRef(false);

  // Create form state
  const [syncedGcalIds, setSyncedGcalIds] = useState(() => {
    try {
      const s = localStorage.getItem('cvflow_gcal_synced_ids');
      return s ? new Set(JSON.parse(s)) : new Set();
    } catch { return new Set(); }
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const prevGoogleRef = useRef(false);

  const [eventsOffen, setEventsOffen]          = useState(false);
  const [showCreate, setShowCreate]           = useState(false);
  const [neuerTitel, setNeuerTitel]           = useState('');
  const [neueVon, setNeueVon]                 = useState('');
  const [neueBis, setNeueBis]                 = useState('');
  const [pushFehler, setPushFehler]           = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [g, m] = await Promise.all([gcalStatus(), msCalStatus()]);
      setGoogleVerbunden(g.data.verbunden);
      setMsVerbunden(m.data.verbunden);
    } catch {}
  }, []);

  const fetchEvents = useCallback(async () => {
    if (!googleVerbunden && !msVerbunden) { setEvents([]); return; }
    try {
      const von = ymd(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1));
      const bis = ymd(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0));
      const reqs = [];
      if (googleVerbunden) reqs.push(gcalEvents(von, bis));
      if (msVerbunden) reqs.push(msCalEvents(von, bis));
      const results = await Promise.all(reqs);
      setEvents(results.flatMap(r => r.data.events || []));
    } catch {}
  }, [googleVerbunden, msVerbunden, viewDate]);

  const persistLocalEvents = useCallback((nextMap) => {
    setLocalEvents(nextMap);
    try { localStorage.setItem('cvflow_cal_events', JSON.stringify(nextMap)); } catch {}
    updateAnalyticsCalendarEvents(calendarMapToApi(nextMap)).catch(() => {});
  }, []);

  const fetchLocalEvents = useCallback(async () => {
    try {
      const res = await getAnalyticsCalendarEvents();
      const remoteEvents = res.data?.events || [];
      if (remoteEvents.length > 0) {
        const nextMap = apiToCalendarMap(remoteEvents);
        setLocalEvents(nextMap);
        try { localStorage.setItem('cvflow_cal_events', JSON.stringify(nextMap)); } catch {}
        localEventMigrationDone.current = true;
        return;
      }
      const saved = localStorage.getItem('cvflow_cal_events');
      if (!localEventMigrationDone.current && saved) {
        const migrated = JSON.parse(saved);
        setLocalEvents(migrated);
        updateAnalyticsCalendarEvents(calendarMapToApi(migrated)).catch(() => {});
      } else {
        setLocalEvents({});
        try { localStorage.setItem('cvflow_cal_events', JSON.stringify({})); } catch {}
      }
      localEventMigrationDone.current = true;
    } catch {
      try {
        const saved = localStorage.getItem('cvflow_cal_events');
        if (saved) setLocalEvents(JSON.parse(saved));
      } catch {}
    }
  }, []);

  const interviewMap = useMemo(() => {
    const map = {};
    stellen.filter(s => s.interview_datum).forEach(s => {
      const key = s.interview_datum.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push({
        time: s.interview_uhrzeit || '',
        title: `Interview · ${s.empfaenger_unternehmen || s.name || 'Unbenannt'}`,
        sub: s.bewerbungs_status === 'kennenlerngespraech' ? 'Kennenlerngespräch' : '',
      });
    });
    return map;
  }, [stellen]);

  const calEventMap = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const key = e.datum || '';
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push({
        _id:    e.id,
        _type:  e.type,
        time:   e.uhrzeit || '',
        title:  e.text || 'Termin',
        sub:    e.type === 'google' ? 'Google Calendar' : e.type === 'microsoft' ? 'Outlook' : '',
      });
    });
    return map;
  }, [events]);

  const syncInterviewsZuGcal = useCallback(async (stellenListe, bereitsGesyncedIds) => {
    const zuSyncen = stellenListe.filter(s => s.interview_datum && !bereitsGesyncedIds.has(s.id));
    if (!zuSyncen.length) return;
    setIsSyncing(true);
    const neuGesyncte = new Set(bereitsGesyncedIds);
    for (const s of zuSyncen) {
      try {
        const titel = `Interview · ${s.empfaenger_unternehmen || s.name || 'Unbenannt'}`;
        await gcalEventErstellen(s.interview_datum, titel, s.interview_uhrzeit || null, null);
        neuGesyncte.add(s.id);
      } catch {}
    }
    setSyncedGcalIds(neuGesyncte);
    try { localStorage.setItem('cvflow_gcal_synced_ids', JSON.stringify([...neuGesyncte])); } catch {}
    setIsSyncing(false);
    setTimeout(fetchEvents, 1500);
  }, [fetchEvents]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchLocalEvents(); }, [fetchLocalEvents]);
  useEffect(() => {
    const onFocus = () => fetchLocalEvents();
    const onVisible = () => {
      if (!document.hidden) fetchLocalEvents();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchLocalEvents]);

  // Wenn Google Calendar (neu) verbunden wird → bestehende Interviews synchronisieren
  useEffect(() => {
    if (googleVerbunden && !prevGoogleRef.current) {
      syncInterviewsZuGcal(stellen, syncedGcalIds);
    }
    prevGoogleRef.current = googleVerbunden;
  }, [googleVerbunden]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close create form when day changes
  useEffect(() => {
    setShowCreate(false);
    setNeuerTitel('');
    setNeueVon('');
    setNeueBis('');
  }, [selected]);

  const handleDeleteEvent = useCallback(async (event) => {
    if (!await confirm('Termin wirklich löschen?')) return;
    if (event._source === 'local') {
      const nextMap = {
        ...localEvents,
        [selected]: (localEvents[selected] || []).filter((_, i) => i !== event._localIdx),
      };
      persistLocalEvents(nextMap);
    } else if (event._source === 'google' && event._id) {
      setEvents(prev => prev.filter(e => e.id !== event._id));
      try {
        await gcalEventLoeschen(event._id);
        setTimeout(fetchEvents, 1200);
      } catch (err) {
        console.error('[CVFlow] Event-Löschung fehlgeschlagen:', err);
        fetchEvents();
      }
    } else if (event._source === 'microsoft' && event._id) {
      setEvents(prev => prev.filter(e => e.id !== event._id));
      try {
        await msCalEventLoeschen(event._id);
        setTimeout(fetchEvents, 1200);
      } catch (err) {
        console.error('[CVFlow] Event-Löschung fehlgeschlagen:', err);
        fetchEvents();
      }
    }
  }, [selected, fetchEvents, localEvents, persistLocalEvents]);

  const changeMonth = (offset) => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    setEventsOffen(false);
  };

  const goToday = () => {
    setViewDate(new Date());
    setSelected(ymd(new Date()));
  };

  const handleDisconnect = async (provider) => {
    if (provider === 'google') {
      await gcalDisconnect();
      setSyncedGcalIds(new Set());
      try { localStorage.removeItem('cvflow_gcal_synced_ids'); } catch {}
    } else {
      await msCalDisconnect();
    }
    fetchStatus();
  };

  const handleConnect = async (provider) => {
    try {
      const res = provider === 'google' ? await gcalAuthUrl() : await msCalAuthUrl();
      const popup = window.open(res.data.url, '_blank', 'width=600,height=700');
      const poll = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(poll);
          fetchStatus();
        }
      }, 1000);
      setTimeout(() => clearInterval(poll), 120_000);
    } catch (err) {
      console.error('[CVFlow] Kalender-Verbindung fehlgeschlagen:', err?.response?.data || err?.message || err);
    }
  };

  const erstelleTermin = useCallback(async () => {
    if (!neuerTitel.trim()) return;
    const newEvent = {
      id: `local_${Date.now()}`,
      time: neueVon || '',
      title: neuerTitel.trim(),
      sub: '',
      uhrzeit_bis: neueBis || '',
    };

    setNeuerTitel('');
    setNeueVon('');
    setNeueBis('');
    setShowCreate(false);

    // Push to connected calendar if available
    setPushFehler(false);
    if (googleVerbunden || msVerbunden) {
      try {
        const fn = googleVerbunden ? gcalEventErstellen : msCalEventErstellen;
        await fn(selected, newEvent.title, neueVon || null, neueBis || null);
        setTimeout(fetchEvents, 1500);
      } catch (err) {
        console.error('[CVFlow] Kalender-Push fehlgeschlagen:', err);
        setPushFehler(true);
        // Fallback: lokal speichern wenn Push fehlschlägt
        persistLocalEvents({
          ...localEvents,
          [selected]: [...(localEvents[selected] || []), newEvent],
        });
      }
    } else {
      persistLocalEvents({
        ...localEvents,
        [selected]: [...(localEvents[selected] || []), newEvent],
      });
    }
  }, [neuerTitel, neueVon, neueBis, selected, googleVerbunden, msVerbunden, fetchEvents, localEvents, persistLocalEvents]);

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDow    = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysPrev    = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = firstDow - 1; i >= 0; i--) {
    cells.push({ d: daysPrev - i, muted: true, date: ymd(new Date(year, month - 1, daysPrev - i)) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ d, muted: false, date: ymd(new Date(year, month, d)) });
  }
  let nextD = 1;
  while (cells.length % 7 !== 0 || cells.length < 35) {
    cells.push({ d: nextD, muted: true, date: ymd(new Date(year, month + 1, nextD)) });
    nextD++;
    if (cells.length >= 42) break;
  }

  const todayStr = ymd(new Date());

  const selectedEvents = [
    ...(interviewMap[selected] || []).map(e => ({ ...e, _source: 'interview' })),
    ...(calEventMap[selected] || []).map(e => ({ ...e, _source: e._type })),
    ...(localEvents[selected] || []).map((e, i) => ({ ...e, _source: 'local', _localIdx: i })),
  ];

  return (
    <div className="an-card an-calendar" style={{
      transform: eventsOffen ? 'translateY(-80px)' : 'translateY(0)',
      transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
    }}>
      <div className="an-card-head" style={{marginBottom: 6}}>
        <div className="an-cal-providers">
          <button
            className={`an-cal-provider-btn${googleVerbunden ? ' an-cal-provider-btn--active an-cal-provider-btn--connected' : ''}${isSyncing ? ' an-cal-provider-btn--syncing' : ''}`}
            onClick={() => googleVerbunden ? handleDisconnect('google') : handleConnect('google')}
            disabled={isSyncing}
          >
            {isSyncing
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-4.5-7.8" style={{animation:'an-spin 0.7s linear infinite',transformOrigin:'center'}}/></svg>
              : <GoogleCalendarIcon size={20} />
            }
            {googleVerbunden && !isSyncing && (
              <span className="an-cal-provider-label">
                <span className="an-cal-provider-label--default">Verbunden</span>
                <span className="an-cal-provider-label--hover">Trennen</span>
              </span>
            )}
          </button>
          <button
            className={`an-cal-provider-btn${msVerbunden ? ' an-cal-provider-btn--active an-cal-provider-btn--connected' : ''}`}
            onClick={() => msVerbunden ? handleDisconnect('ms') : handleConnect('ms')}
          >
            <MicrosoftCalendarIcon size={20} />
            {msVerbunden && (
              <span className="an-cal-provider-label">
                <span className="an-cal-provider-label--default">Verbunden</span>
                <span className="an-cal-provider-label--hover">Trennen</span>
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="an-cal-head">
        <div className="an-cal-month">{MONATE_DE[month]} {year}</div>
        <div className="an-cal-nav">
          <button className="an-cal-nav-btn" onClick={() => changeMonth(-1)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button className="an-cal-nav-btn an-cal-today-btn" onClick={goToday}>Heute</button>
          <button className="an-cal-nav-btn" onClick={() => changeMonth(1)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </div>

      <div className="an-cal-grid">
        {DOW_DE.map(d => <div key={d} className="an-cal-dow">{d}</div>)}
        {cells.map((cell, idx) => {
          const hasEvent = !!(interviewMap[cell.date]?.length) || !!(calEventMap[cell.date]?.length) || !!(localEvents[cell.date]?.length);
          const cls = [
            'an-cal-day',
            cell.muted  ? 'an-cal-day--muted'     : '',
            cell.date === todayStr     ? 'an-cal-day--today'     : '',
            hasEvent                   ? 'an-cal-day--has-event' : '',
            cell.date === selected && cell.date !== todayStr ? 'an-cal-day--selected' : '',
          ].filter(Boolean).join(' ');
          return (
            <div key={idx} className={cls} onClick={() => { if (!cell.muted) { if (cell.date === selected && eventsOffen) { setEventsOffen(false); } else { setSelected(cell.date); setEventsOffen(true); } } }}>
              {cell.d}
              {hasEvent && <span className="an-cal-dot" />}
            </div>
          );
        })}
      </div>

      <div className="an-cal-events">
        {selectedEvents.length === 0 ? (
          <div className="an-cal-event-empty">Keine Termine an diesem Tag.</div>
        ) : (
          selectedEvents.map((e, i) => (
            <div key={i} className="an-cal-event">
              <div className="an-cal-event-time">{e.time || '—'}</div>
              <div className="an-cal-event-body">
                <div className="an-cal-event-title">{e.title}</div>
                {e.sub && <div className="an-cal-event-sub">{e.sub}</div>}
              </div>
              {e._source !== 'interview' && (
                <button
                  onClick={() => handleDeleteEvent(e)}
                  title="Termin löschen"
                  style={{
                    marginLeft: 'auto', flexShrink: 0,
                    background: 'none', border: 'none',
                    cursor: 'pointer', padding: '2px 4px',
                    color: 'var(--an-text-muted)', borderRadius: 4,
                    opacity: 0.5, transition: 'opacity 0.15s, color 0.15s',
                  }}
                  onMouseEnter={ev => { ev.currentTarget.style.opacity = '1'; ev.currentTarget.style.color = '#c0392b'; }}
                  onMouseLeave={ev => { ev.currentTarget.style.opacity = '0.5'; ev.currentTarget.style.color = 'var(--an-text-muted)'; }}
                >
                  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                    <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          ))
        )}

        {/* ── Create event ── */}
        {showCreate ? (
          <div style={{
            padding: '12px 14px', marginTop: 4,
            background: 'rgba(0,77,64,0.04)',
            border: '1px solid rgba(0,77,64,0.14)',
            borderRadius: 12,
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--an-text-muted)',
              marginBottom: 10,
            }}>
              {formatDay(selected)}
            </div>
            <input
              autoFocus
              value={neuerTitel}
              onChange={e => setNeuerTitel(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') erstelleTermin();
                if (e.key === 'Escape') setShowCreate(false);
              }}
              placeholder="Titel des Termins"
              style={{ ...inputBase, marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
              <input
                type="time"
                value={neueVon}
                onChange={e => setNeueVon(e.target.value)}
                style={{ ...inputBase, width: 'auto', flex: 1, fontSize: 12, padding: '6px 8px' }}
              />
              <span style={{ fontSize: 11, color: 'var(--an-text-muted)', flexShrink: 0 }}>bis</span>
              <input
                type="time"
                value={neueBis}
                onChange={e => setNeueBis(e.target.value)}
                style={{ ...inputBase, width: 'auto', flex: 1, fontSize: 12, padding: '6px 8px' }}
              />
            </div>
            {(googleVerbunden || msVerbunden) && (
              <div style={{ fontSize: 10, color: 'var(--an-teal)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Wird in deinen Kalender eingetragen
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreate(false)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  border: '1px solid rgba(0,0,0,0.10)', background: 'none',
                  fontSize: 12, fontWeight: 600, color: 'var(--an-text-soft)',
                  cursor: 'pointer', fontFamily: 'var(--an-font-body)',
                }}
              >Abbrechen</button>
              <button
                onClick={erstelleTermin}
                disabled={!neuerTitel.trim()}
                style={{
                  padding: '6px 14px', borderRadius: 999,
                  border: 'none', background: 'var(--an-teal)',
                  fontSize: 12, fontWeight: 700, color: '#fff',
                  cursor: neuerTitel.trim() ? 'pointer' : 'not-allowed',
                  opacity: neuerTitel.trim() ? 1 : 0.45,
                  fontFamily: 'var(--an-font-body)',
                }}
              >Speichern</button>
            </div>
          </div>
        ) : (
          <>
            {pushFehler && (
              <div style={{
                fontSize: 11, color: '#c0392b', background: 'rgba(192,57,43,0.07)',
                border: '1px solid rgba(192,57,43,0.2)', borderRadius: 8,
                padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M8 5v3.5M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Kalender-Sync fehlgeschlagen. Bitte erneut verknüpfen.
              </div>
            )}
            <button className="an-cal-add-btn" onClick={() => setShowCreate(true)}>
              <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Termin hinzufügen
            </button>
          </>
        )}
      </div>
    </div>
  );
}
